// Parlay slip context — shared state for the floating builder sheet.
// Legs are persisted to localStorage so a refresh doesn't nuke the slip.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { readSlipFromUrl } from "@/lib/parlayShare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MIN_LEGS = 2;
export const MAX_LEGS = 8;
export const DEFAULT_BANKROLL = 1000;
const STORAGE_LEGS = "tracker.parlay.legs.v1";
const STORAGE_BANKROLL = "tracker.parlay.bankroll.v1";

const Ctx = createContext(null);

export function legKey(leg) {
  if (leg.kind === "prop") {
    return [
      "prop",
      (leg.sport || "").toLowerCase(),
      (leg.event_id || "").toLowerCase(),
      (leg.market_key || "").toLowerCase(),
      (leg.player || "").toLowerCase(),
      String(leg.point ?? ""),
      (leg.side || "").toLowerCase(),
    ].join("|");
  }
  return [
    "ml",
    (leg.sport || "").toLowerCase(),
    (leg.home_team || "").toLowerCase(),
    (leg.away_team || "").toLowerCase(),
    (leg.date || "tbd").toLowerCase().slice(0, 10),
    (leg.side || "").toLowerCase(),
  ].join("|");
}

function readLegs() {
  // Deep-linked slip (?slip=…) wins over localStorage on first load so
  // opening a shared URL always shows the sender's picks.
  const urlLegs = readSlipFromUrl();
  if (urlLegs && urlLegs.length > 0) return urlLegs.slice(0, MAX_LEGS);
  try {
    const raw = localStorage.getItem(STORAGE_LEGS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_LEGS) : [];
  } catch { return []; }
}

function readBankroll() {
  try {
    const v = Number(localStorage.getItem(STORAGE_BANKROLL));
    return v > 0 && Number.isFinite(v) ? v : DEFAULT_BANKROLL;
  } catch { return DEFAULT_BANKROLL; }
}

export function ParlayProvider({ children }) {
  const [legs, setLegs] = useState(readLegs);
  const [bankroll, setBankroll] = useState(readBankroll);
  const [open, setOpen] = useState(() => {
    // Auto-open the sheet when the user lands via a shared slip URL
    try { return readSlipFromUrl().length > 0; } catch { return false; }
  });
  const [pricing, setPricing] = useState(null); // last priced response
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_LEGS, JSON.stringify(legs)); } catch {}
  }, [legs]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_BANKROLL, String(bankroll)); } catch {}
  }, [bankroll]);

  // Invalidate pricing whenever the legs change
  useEffect(() => { setPricing(null); setPricingError(null); }, [legs]);

  function hasLeg(leg) {
    const k = legKey(leg);
    return legs.some((l) => legKey(l) === k);
  }

  function sideFor(leg) {
    // Return the picked side ('home'/'away') for the given game, or null.
    const base = legs.find((l) =>
      (l.sport || "").toLowerCase() === (leg.sport || "").toLowerCase() &&
      (l.home_team || "").toLowerCase() === (leg.home_team || "").toLowerCase() &&
      (l.away_team || "").toLowerCase() === (leg.away_team || "").toLowerCase() &&
      ((l.date || "tbd").toLowerCase().slice(0, 10) ===
        (leg.date || "tbd").toLowerCase().slice(0, 10))
    );
    return base?.side || null;
  }

  function addLeg(leg) {
    const k = legKey(leg);
    const sameGameOtherSide = legs.find((l) =>
      (l.sport || "").toLowerCase() === (leg.sport || "").toLowerCase() &&
      (l.home_team || "").toLowerCase() === (leg.home_team || "").toLowerCase() &&
      (l.away_team || "").toLowerCase() === (leg.away_team || "").toLowerCase() &&
      ((l.date || "tbd").toLowerCase().slice(0, 10) ===
        (leg.date || "tbd").toLowerCase().slice(0, 10)) &&
      legKey(l) !== k
    );
    setLegs((prev) => {
      let next = prev;
      if (sameGameOtherSide) {
        // Replace existing side pick for the same game
        next = next.filter((l) => legKey(l) !== legKey(sameGameOtherSide));
      }
      if (next.some((l) => legKey(l) === k)) return next;
      if (next.length >= MAX_LEGS) return next;
      return [...next, leg];
    });
    return { capped: legs.length >= MAX_LEGS && !sameGameOtherSide };
  }

  function removeLeg(leg) {
    const k = legKey(leg);
    setLegs((prev) => prev.filter((l) => legKey(l) !== k));
  }

  function clearLegs() { setLegs([]); setPricing(null); setPricingError(null); }

  async function priceParlay() {
    if (legs.length < MIN_LEGS) {
      setPricingError(`Add at least ${MIN_LEGS} legs to price a parlay.`);
      return null;
    }
    setPricingLoading(true);
    setPricingError(null);
    try {
      const resp = await axios.post(`${API}/betting/parlay`, {
        legs: legs.map((l) => ({
          sport: l.sport,
          home_team: l.home_team,
          away_team: l.away_team,
          date: l.date || null,
          time: l.time || null,
          side: l.side,
        })),
      }, { timeout: 60000 });
      setPricing(resp.data);
      return resp.data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not price parlay.";
      setPricingError(typeof msg === "string" ? msg : JSON.stringify(msg));
      return null;
    } finally {
      setPricingLoading(false);
    }
  }

  const value = useMemo(() => ({
    legs, bankroll, setBankroll,
    open, setOpen,
    pricing, pricingLoading, pricingError,
    hasLeg, sideFor, addLeg, removeLeg, clearLegs, priceParlay,
    canAdd: legs.length < MAX_LEGS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [legs, bankroll, open, pricing, pricingLoading, pricingError]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useParlay() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useParlay must be used inside ParlayProvider");
  return v;
}
